import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {notifyError, notifySuccess} from '../../../utils/notifications';

const keyOf = (deviceId, itemId) => `${deviceId}:${itemId}`;

/**
 * Hromadné vyplnění časové náročnosti oprav v minutách (zařízení × opravy).
 * Z minut se počítá kapacita servisu pro rezervace z webu.
 */
const RepairTimes = () => {
    const [devices, setDevices] = useState(null);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(false);
    const [edits, setEdits] = useState({});
    const [columnFill, setColumnFill] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const {data} = await API.get(API_ENDPOINTS.device.repairTimes, {meta: {silentError: true}});
            setDevices(data || []);
            setEdits({});
        } catch (e) {
            notifyError('Opravy se nepodařilo načíst.');
        }
    };

    useEffect(() => {
        load();
    }, []);

    const dirtyCount = Object.keys(edits).length;
    useEffect(() => {
        if (!dirtyCount) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirtyCount]);

    const categories = useMemo(() => {
        const map = new Map();
        (devices || []).forEach((device) => device.categories.forEach((item) => map.set(String(item._id), item.name)));
        return [...map.entries()].map(([value, label]) => ({value, label})).sort((a, b) => a.label.localeCompare(b.label, 'cs'));
    }, [devices]);

    useEffect(() => {
        if (!category && categories.length) setCategory(categories[0].value);
    }, [categories, category]);

    const valueOf = (device, item) => {
        const key = keyOf(device._id, item.itemId);
        return key in edits ? edits[key] : (item.minutes ?? '');
    };

    const visibleDevices = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (devices || []).filter((device) =>
            (!category || device.categories.some((item) => String(item._id) === category))
            && (!q || `${device.name} ${device.code || ''}`.toLowerCase().includes(q))
            && (!onlyMissing || device.repairs.some((item) => !item.minutes)));
    }, [devices, category, search, onlyMissing]);

    const columns = useMemo(() => {
        const map = new Map();
        visibleDevices.forEach((device) => device.repairs.forEach((item) => {
            const column = map.get(String(item.repairId)) || {repairId: String(item.repairId), name: item.repairName, catalogMinutes: item.catalogMinutes, count: 0};
            column.count += 1;
            map.set(String(item.repairId), column);
        }));
        return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'cs'));
    }, [visibleDevices]);

    const stats = useMemo(() => {
        let total = 0;
        let filled = 0;
        (devices || []).forEach((device) => device.repairs.forEach((item) => {
            total += 1;
            const value = valueOf(device, item);
            if (Number(value) > 0) filled += 1;
        }));
        return {total, filled};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices, edits]);

    const setCell = (device, item, raw) => {
        const key = keyOf(device._id, item.itemId);
        const value = raw === '' ? '' : Math.max(0, Math.round(Number(raw)));
        setEdits((prev) => {
            const next = {...prev};
            if ((value === '' && !item.minutes) || value === item.minutes) delete next[key];
            else next[key] = value;
            return next;
        });
    };

    /** Prázdná pole ve výběru vyplní odhadem z původního textu („30 min“, „1-2 hod“…). */
    const applySuggestions = () => {
        const next = {...edits};
        let count = 0;
        visibleDevices.forEach((device) => device.repairs.forEach((item) => {
            const key = keyOf(device._id, item.itemId);
            if (!item.minutes && item.suggested && !(key in next)) {
                next[key] = item.suggested;
                count += 1;
            }
        }));
        setEdits(next);
        notifySuccess(count ? `Předvyplněno ${count} hodnot – zkontrolujte a uložte.` : 'Žádné odhady k převzetí.');
    };

    const fillColumn = (column) => {
        const minutes = Math.round(Number(columnFill[column.repairId]));
        if (!(minutes > 0)) return;
        const next = {...edits};
        visibleDevices.forEach((device) => device.repairs
            .filter((item) => String(item.repairId) === column.repairId && !Number(valueOf(device, item)))
            .forEach((item) => {
                next[keyOf(device._id, item.itemId)] = minutes;
            }));
        setEdits(next);
    };

    const save = async () => {
        const changes = Object.entries(edits).map(([key, minutes]) => {
            const [deviceId, itemId] = key.split(':');
            return {deviceId, itemId, minutes: minutes === '' ? 0 : minutes};
        });
        setSaving(true);
        try {
            const {data} = await API.put(API_ENDPOINTS.device.repairTimes, {changes}, {meta: {silentSuccess: true, silentError: true}});
            notifySuccess(`Uloženo ${data?.matched ?? changes.length} hodnot`);
            await load();
        } catch (e) {
            notifyError(e?.response?.data?.errorMessage || 'Uložení se nepovedlo.');
        } finally {
            setSaving(false);
        }
    };

    if (!devices) return <div className="p-4 text-sm text-gray-500">Načítám…</div>;

    return (
        <div className="space-y-4 pb-20">
            <div>
                <h1 className="text-xl font-semibold">Časová náročnost oprav</h1>
                <p className="max-w-3xl text-sm text-gray-600">
                    Kolik minut práce oprava zabere. Z toho se počítá, kolik rezervací z webu se do dne vejde
                    (<Link to={routes.admin.inquiries.settingsFull} className="text-indigo-700 hover:underline">otevírací doba a lidé na servisu</Link>).
                    Prázdné pole = použije se výchozí hodnota opravy z katalogu, případně z nastavení rezervací.
                </p>
                <p className="mt-1 text-sm">
                    Vyplněno <strong>{stats.filled}</strong> z {stats.total} oprav u všech zařízení.
                </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm">
                    <span className="mb-1 block text-xs text-gray-500">Kategorie</span>
                    <select value={category} onChange={(event) => setCategory(event.target.value)} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm">
                        {categories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        <option value="">Všechny (široká tabulka)</option>
                    </select>
                </label>
                <label className="text-sm">
                    <span className="mb-1 block text-xs text-gray-500">Zařízení</span>
                    <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Hledat model…" className="w-56 rounded-md border border-gray-300 px-2 py-1.5 text-sm"/>
                </label>
                <label className="flex items-center gap-1.5 pb-2 text-sm">
                    <input type="checkbox" checked={onlyMissing} onChange={(event) => setOnlyMissing(event.target.checked)}/>
                    Jen zařízení s nevyplněnými opravami
                </label>
                <button type="button" onClick={applySuggestions} className="ml-auto rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50" title="Převezme hodnoty z původního textového zápisu (např. „30 min“, „1-2 hod“)">
                    Předvyplnit z původních textů
                </button>
            </div>

            <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-white">
                <table className="text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                        <tr className="text-left align-bottom text-xs text-gray-600">
                            <th className="sticky left-0 z-20 min-w-[12rem] bg-gray-50 px-3 py-2 font-medium">Zařízení</th>
                            {columns.map((column) => (
                                <th key={column.repairId} className="min-w-[8.5rem] border-l border-gray-100 px-2 py-2 font-medium">
                                    <div className="line-clamp-2" title={column.name}>{column.name}</div>
                                    {column.catalogMinutes ? <div className="font-normal text-gray-400">katalog: {column.catalogMinutes} min</div> : null}
                                    <div className="mt-1 flex gap-1">
                                        <input
                                            type="number"
                                            min={1}
                                            placeholder="min"
                                            value={columnFill[column.repairId] || ''}
                                            onChange={(event) => setColumnFill((prev) => ({...prev, [column.repairId]: event.target.value}))}
                                            className="w-14 rounded border border-gray-300 px-1 py-0.5 font-normal"
                                        />
                                        <button type="button" onClick={() => fillColumn(column)} className="rounded border border-gray-300 bg-white px-1.5 font-normal hover:bg-gray-100" title="Vyplnit prázdná pole ve sloupci">
                                            vyplnit
                                        </button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleDevices.map((device) => {
                            const byRepair = new Map(device.repairs.map((item) => [String(item.repairId), item]));
                            return (
                                <tr key={device._id} className="border-t border-gray-100">
                                    <th className="sticky left-0 bg-white px-3 py-1.5 text-left font-medium">
                                        {device.name}
                                        {device.code ? <span className="ml-1 text-xs font-normal text-gray-400">{device.code}</span> : null}
                                    </th>
                                    {columns.map((column) => {
                                        const item = byRepair.get(column.repairId);
                                        if (!item) return <td key={column.repairId} className="border-l border-gray-100 bg-gray-50"/>;
                                        const key = keyOf(device._id, item.itemId);
                                        const changed = key in edits;
                                        const value = valueOf(device, item);
                                        return (
                                            <td key={column.repairId} className={`border-l border-gray-100 px-2 py-1 ${changed ? 'bg-amber-50' : ''}`}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={5}
                                                    value={value}
                                                    placeholder={item.suggested ? `≈ ${item.suggested}` : column.catalogMinutes ? String(column.catalogMinutes) : '–'}
                                                    onChange={(event) => setCell(device, item, event.target.value)}
                                                    className={`w-20 rounded border px-1.5 py-0.5 ${changed ? 'border-amber-400' : Number(value) > 0 ? 'border-gray-300' : 'border-dashed border-gray-300'} ${item.active ? '' : 'opacity-50'}`}
                                                    aria-label={`${device.name} – ${item.repairName} (minuty)`}
                                                    title={item.raw != null && typeof item.raw !== 'number' ? `Původně: „${item.raw}“` : undefined}
                                                />
                                                {item.raw != null && typeof item.raw !== 'number' && String(item.raw).trim() ? (
                                                    <div className="mt-0.5 max-w-[7.5rem] truncate text-[11px] text-gray-400" title={String(item.raw)}>„{String(item.raw)}“</div>
                                                ) : null}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {!visibleDevices.length ? (
                            <tr><td colSpan={columns.length + 1} className="p-6 text-center text-gray-500">Žádná zařízení neodpovídají filtru.</td></tr>
                        ) : null}
                    </tbody>
                </table>
            </div>

            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:left-64">
                <div className="flex items-center justify-end gap-3">
                    {dirtyCount ? <span className="text-sm text-amber-700">Neuložené změny: {dirtyCount}</span> : <span className="text-sm text-gray-500">Žádné změny</span>}
                    <button type="button" disabled={!dirtyCount || saving} onClick={() => setEdits({})} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-40">
                        Zahodit
                    </button>
                    <button type="button" disabled={!dirtyCount || saving} onClick={save} className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? 'Ukládám…' : 'Uložit změny'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RepairTimes;
